import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Box, Paper, Tabs, Tab, Typography } from '@mui/material';

// Restaurant Masters workspace — WORKFLOW CONSOLIDATION ONLY.
//
// A thin horizontal tab strip over the nine existing restaurant master pages.
// Each tab simply navigates to the page's EXISTING route, so URLs, bookmarks,
// the sidebar links, permissions and every page component stay exactly as
// they are. This file adds navigation; it changes nothing else.
//
// Visibility mirrors the sidebar's rule for the same items (AdminLayout):
// master admins see everything; otherwise a tab shows only when the user's
// sessionStorage permission grants contain its menuKey. Fail-open like the
// sidebar guard — no permission list stored -> nothing is hidden.
const TABS = [
  { label: 'Department', path: '/admin/AddDepartment', menuKey: 'AllCategory' },
  { label: 'Area', path: '/admin/Createarea', menuKey: 'Createarea' },
  { label: 'Categories', path: '/admin/Category', menuKey: 'Category' },
  { label: 'Items', path: '/admin/AddItem', menuKey: 'AddItem' },
  {
    label: 'Variants',
    path: '/admin/VariantManagement',
    menuKey: 'VariantManagement',
  },
  // Swiggy / Zomato (WeraFoods). Lives here, next to the items it sells,
  // instead of its own sidebar entry. Its own key: pushing the menu online is
  // not something everyone who can edit items should be able to do.
  {
    label: 'Online Ordering',
    path: '/admin/OnlineOrdering',
    menuKey: 'OnlineOrdering',
  },
  { label: 'Store', path: '/admin/StoreMaster', menuKey: 'StoreMaster' },
  // Back as a tab (2026-09-17, on request). It had been removed in favour of
  // Recipes (Store menu), which manages the same item → store-ingredient
  // mappings; both screens stay. Its own key, so it shows only to a user
  // granted Item Store Mapping.
  {
    label: 'Store Mapping',
    path: '/admin/ItemStoreMapping',
    menuKey: 'ItemStoreMapping',
  },
  // 'Store' (StoreMaster) tab removed — Store Items now lives in the Store /
  // Inventory tab strip. The route still exists there, so nothing breaks.
  // 'Store Mapping' (ItemStoreMapping) tab removed — Recipes (Store menu) is now
  // the single place to manage menu-item → store-ingredient mappings. The
  // ItemStoreMapping route still exists (URL-reachable) so nothing breaks.
  { label: 'Area Pricing', path: '/admin/AreaPricing', menuKey: 'AreaPricing' },
  {
    label: 'Menu Upload History',
    path: '/admin/MenuUploadHistory',
    menuKey: 'AddItem',
  },
];

const allowedTabs = () => {
  let user = null;
  try {
    user = JSON.parse(sessionStorage.getItem('user'));
  } catch (_) {}
  if (user?.isMaster) return TABS;

  const raw = sessionStorage.getItem('permissions');
  if (!raw) return TABS; // fail-open, same as the sidebar guard
  try {
    const keys = new Set(JSON.parse(raw).map((p) => p.menuKey));
    return TABS.filter((t) => keys.has(t.menuKey));
  } catch (_) {
    return TABS;
  }
};

// The header search offers these tabs too, and reads them through the SAME
// filter the strip uses - so it can never offer a tab the strip would hide,
// and a tab added above turns up in search without being listed twice.
export const allowedRestaurantSetupTabs = () => allowedTabs();

export default function RestaurantMastersLayout() {
  const location = useLocation();
  const navigate = useNavigate();

  const tabs = React.useMemo(allowedTabs, []);
  const current = location.pathname.replace(/\/+$/, '');
  const activeIdx = tabs.findIndex(
    (t) => current === t.path || current.startsWith(t.path + '/'),
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Paper
        elevation={0}
        square
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: { xs: 1, sm: 2 },
          pt: 1,
          bgcolor: 'background.paper',
        }}>
        <Typography
          variant='subtitle2'
          sx={{ fontWeight: 700, color: 'text.secondary', mb: 0.5 }}>
          Restaurant Setup
        </Typography>
        <Tabs
          value={activeIdx === -1 ? false : activeIdx}
          onChange={(_, idx) => navigate(tabs[idx].path)}
          variant='scrollable'
          scrollButtons='auto'
          allowScrollButtonsMobile
          sx={{
            minHeight: 40,
            '& .MuiTab-root': {
              minHeight: 40,
              textTransform: 'none',
              fontWeight: 600,
            },
          }}>
          {tabs.map((t) => (
            <Tab key={t.path} label={t.label} />
          ))}
        </Tabs>
      </Paper>

      {/* The existing page renders below, completely unchanged. */}
      <Outlet />
    </Box>
  );
}